import { Injectable } from "@angular/core";
import { GuidUtils } from "../utils/guid-utils";

@Injectable({
    providedIn: "root"
})
export abstract class EventBridgeService {
    protected readonly subscriptions: Map<string, IBridgeEvent[]> = new Map<string, IBridgeEvent[]>();

    destroy() {
        this.subscriptions.clear();
    }

    subscribeMany<T>(events: string[], callback: (payload: T) => void) {
        events.forEach(e => {
            this.subscribe(e, callback);
        });
    }

    subscribe<T>(event: string, callback: (payload: T) => void): IBridgeEvent {
        const guid = GuidUtils.newGuid();
        const ev = {
            event,
            callback,
            guid
        };
        if (this.subscriptions.has(event)) {
            this.subscriptions.get(event).push(ev);
        } else {
            this.subscriptions.set(event, [ev]);
        }
        return ev;
    }

    unsubscribe(event: IBridgeEvent): void {
        if (!event) {
            return;
        }
        if (!this.subscriptions.has(event.event)) {
            return;
        }
        const eventSubscribers = this.subscriptions.get(event.event);
        const matchingCallbacks = eventSubscribers?.filter(x => x.guid === event.guid);
        matchingCallbacks?.forEach(ev => {
            eventSubscribers.splice(eventSubscribers.indexOf(ev), 1);
        });
        if (eventSubscribers?.length === 0) {
            this.subscriptions.delete(event.event);
        }
    }

    unsubscribeMany(events: IBridgeEvent[]): void {
        if (!events) {
            return;
        }
        events.forEach(event => {
            this.unsubscribe(event);
        });
    }

    emit(emitted, payload) {
        if (this.subscriptions.has(emitted)) {
            this.subscriptions.get(emitted).forEach(ev => ev.callback(payload));
        }
    }
}

export interface IBridgeEvent {
    event: string;
    callback: (payload) => void;
    guid: string;
}
